import React, { memo, useCallback, useEffect, useState } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { ENABLED } from '@atlassian/jira-servicedesk-common/src/service-project-features/constants.tsx';
import type { Feature } from '@atlassian/jira-servicedesk-common/src/service-project-features/types.tsx';
import usePracticeFeaturesController from '../../../../../controllers/practices/practice-features-controller/index.tsx';
import { AsyncConfirmDisableModal } from './confirm-disable-modal/async';
import WorkCategoryFeatureTile from './tile';
import { getPracticeFeatures } from './tile/constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PracticesFeatures = memo<Record<any, any>>(() => {
	const { data, saving, update, paymentError } = usePracticeFeaturesController();
	const [confirmFeature, setConfirmFeature] = useState<string>('');
	const [numAffectedRT, setNumAffectedRT] = useState<number>();
	const [affectedRTError, setAffectedRTError] = useState<Error>();
	const [refocusElement, setRefocusElement] = useState<string>('');

	const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);

	const onEnable = useCallback(
		(feature: Feature) => {
			setConfirmFeature(confirmFeature);
			update({ feature, enabled: true });
		},
		[confirmFeature, update],
	);

	const onDisable = useCallback(
		(feature: Feature, numAffectedRequestTypes?: number, error?: Error) => {
			setConfirmFeature(feature);
			if (error || (numAffectedRequestTypes !== undefined && numAffectedRequestTypes > 0)) {
				// If request types are assigned to the work category, we need to show the disable modal to confirm they will be moved to unassigned before disabling the feature
				setIsDisableModalOpen(true);
				setNumAffectedRT(numAffectedRequestTypes);
				setAffectedRTError(error);
			} else {
				update({ feature, enabled: false });
			}
		},
		[update],
	);

	const practiceFeatures = getPracticeFeatures();

	const onConfirmDisable = useCallback(() => {
		update({ feature: confirmFeature, enabled: false });
		setConfirmFeature('');
		setIsDisableModalOpen(false);
	}, [confirmFeature, update]);

	const closeDisableModal = useCallback(() => {
		setRefocusElement(confirmFeature);
		setConfirmFeature('');
		setIsDisableModalOpen(false);
	}, [confirmFeature]);

	useEffect(() => {
		refocusElement && setRefocusElement('');
	}, [refocusElement]);

	return (
		<>
			{practiceFeatures.map((feature) => (
				<WorkCategoryFeatureTile
					key={feature}
					feature={feature}
					saving={!!saving[feature]}
					isEnabled={data?.[feature]?.status === ENABLED}
					focusToggle={refocusElement === feature}
					onEnable={onEnable}
					onDisable={onDisable}
					{...(ff('enable_jsm_repackaging_changes_vtwk9')
						? {
								hasPaymentError: !!paymentError[feature],
							}
						: {})}
				/>
			))}

			{isDisableModalOpen && (
				<AsyncConfirmDisableModal
					feature={confirmFeature}
					onClose={closeDisableModal}
					onConfirm={onConfirmDisable}
					numAffectedRequestTypes={numAffectedRT}
					affectedRequestTypesError={affectedRTError}
				/>
			)}
		</>
	);
});

export default PracticesFeatures;
